/**
 * Shared Cognitory main-page visual tokens and style primitives.
 * Signed-out landing and selected-Cognitory shell must reuse these so the
 * experience feels like one product surface, not a separate dashboard.
 */

import type {
  CSSProperties,
  FocusEvent,
  MouseEvent as ReactMouseEvent,
} from "react";

export const MAIN_PAGE_FONT_STACK =
  "Inter, -apple-system, BlinkMacSystemFont, sans-serif" as const;

export const MAIN_PAGE_COLORS = {
  pageBackground: "#f8fafc",
  ink: "#1a1a1a",
  inkStrong: "#1e293b",
  muted: "#555",
  slate: "#475569",
  soft: "#64748b",
  whisper: "#94a3b8",
  label: "#4a5568",
  panelBorder: "rgba(148, 163, 184, 0.38)",
  panelFill: "rgba(255, 255, 255, 0.25)",
  inputBorder: "rgba(148, 163, 184, 0.55)",
  inputFill: "rgba(255, 255, 255, 0.72)",
  liquidBorder: "rgba(125, 211, 252, 0.5)",
  quietFill: "rgba(255, 255, 255, 0.55)",
  quietInk: "#334155",
  alertInk: "#b91c1c",
  alertFill: "rgba(254, 226, 226, 0.65)",
  alertBorder: "rgba(252, 165, 165, 0.55)",
  warnInk: "#9a3412",
  warnFill: "rgba(254, 243, 199, 0.7)",
  warnBorder: "rgba(251, 191, 36, 0.55)",
  processingInk: "#4A2A0A",
  processingMid: "#EDC47A",
  processingAccent: "#D99A43",
  processingBorder: "rgba(217, 154, 67, 0.55)",
  link: "#2563eb",
} as const;

export const MAIN_PAGE_RADIUS = {
  panel: "14px",
  input: "10px",
  alert: "8px",
  capsule: "999px",
  recordCard: "5px",
} as const;

export const MAIN_PAGE_RECORD_CARD_STYLE: CSSProperties = {
  borderRadius: MAIN_PAGE_RADIUS.recordCard,
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  background: MAIN_PAGE_COLORS.quietFill,
};

export const MAIN_PAGE_PANEL_STYLE: CSSProperties = {
  padding: "clamp(1.75rem, 3.5vw, 2.35rem) clamp(1.25rem, 3.5vw, 2rem)",
  textAlign: "center",
  backgroundColor: MAIN_PAGE_COLORS.panelFill,
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  borderRadius: MAIN_PAGE_RADIUS.panel,
  boxShadow: "0 4px 18px rgba(15, 23, 42, 0.05)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  position: "relative",
  zIndex: 1,
  width: "100%",
  boxSizing: "border-box",
  overflow: "visible",
  marginBottom: "1.25rem",
};

export const MAIN_PAGE_PANEL_INNER_STYLE: CSSProperties = {
  width: "100%",
  maxWidth: "28rem",
  margin: "0 auto",
  whiteSpace: "normal",
  overflow: "visible",
};

export const MAIN_PAGE_PANEL_HEADING_STYLE: CSSProperties = {
  fontSize: "clamp(1.05rem, 2vw, 1.2rem)",
  color: MAIN_PAGE_COLORS.slate,
  marginBottom: "0.35rem",
  fontWeight: 400,
  letterSpacing: "-0.01em",
  whiteSpace: "normal",
  fontFamily: MAIN_PAGE_FONT_STACK,
};

export const MAIN_PAGE_BODY_STYLE: CSSProperties = {
  marginTop: "0.85rem",
  maxWidth: "28rem",
  marginLeft: "auto",
  marginRight: "auto",
  textAlign: "center",
  color: MAIN_PAGE_COLORS.muted,
  lineHeight: 1.6,
  fontFamily: MAIN_PAGE_FONT_STACK,
};

/** Administrative open action — polished liquid capsule distinct from workspace Ask. */
export const MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE: CSSProperties = {
  marginTop: "0.75rem",
  padding: "0.55rem 1.25rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: "1px solid rgba(96, 165, 250, 0.55)",
  background: [
    "linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 36%, rgba(255,255,255,0) 55%)",
    "linear-gradient(165deg, #60a5fa 0%, #3b82f6 48%, #1d4ed8 100%)",
  ].join(", "),
  color: "#ffffff",
  fontSize: "0.95rem",
  fontWeight: 500,
  letterSpacing: "0.01em",
  cursor: "pointer",
  boxShadow: [
    "inset 0 1px 0 rgba(255,255,255,0.5)",
    "inset 0 -1px 0 rgba(30, 64, 175, 0.2)",
    "0 6px 18px rgba(37, 99, 235, 0.26)",
    "0 2px 6px rgba(15, 23, 42, 0.08)",
  ].join(", "),
  fontFamily: MAIN_PAGE_FONT_STACK,
  textDecoration: "none",
  display: "inline-block",
  transition:
    "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease, border-color 140ms ease",
};

/** Coastal liquid capsule for primary actions (Sign in, Ask, Create Cognitory). */
export const MAIN_PAGE_LIQUID_BUTTON_STYLE: CSSProperties = {
  marginTop: "1.15rem",
  padding: "0.78rem 1.7rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: `1px solid ${MAIN_PAGE_COLORS.liquidBorder}`,
  background: [
    "linear-gradient(180deg, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.08) 36%, rgba(255,255,255,0) 55%)",
    "linear-gradient(165deg, #6fd0f2 0%, #2ea8d8 45%, #1b7fb8 100%)",
  ].join(", "),
  color: "#ffffff",
  fontSize: "1rem",
  fontWeight: 500,
  letterSpacing: "0.01em",
  cursor: "pointer",
  boxShadow: [
    "inset 0 1px 0 rgba(255,255,255,0.55)",
    "inset 0 -1px 0 rgba(12,74,110,0.18)",
    "0 8px 22px rgba(14, 116, 144, 0.28)",
    "0 2px 6px rgba(15, 23, 42, 0.08)",
  ].join(", "),
  fontFamily: MAIN_PAGE_FONT_STACK,
  transition:
    "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease, border-color 140ms ease",
};

export const MAIN_PAGE_QUIET_BUTTON_STYLE: CSSProperties = {
  marginTop: "0.75rem",
  padding: "0.55rem 1.1rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: `1px solid ${MAIN_PAGE_COLORS.inputBorder}`,
  background: MAIN_PAGE_COLORS.quietFill,
  color: MAIN_PAGE_COLORS.quietInk,
  fontSize: "0.95rem",
  fontFamily: MAIN_PAGE_FONT_STACK,
  cursor: "pointer",
};

/** Solid Cognitory brand orange — primary evaluation actions (Save). */
export const MAIN_PAGE_EVALUATION_PRIMARY_BUTTON_STYLE: CSSProperties = {
  marginTop: "0.65rem",
  padding: "0.55rem 1.15rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: `1px solid ${MAIN_PAGE_COLORS.processingAccent}`,
  background: MAIN_PAGE_COLORS.processingAccent,
  color: "#ffffff",
  fontSize: "0.95rem",
  fontWeight: 500,
  fontFamily: MAIN_PAGE_FONT_STACK,
  cursor: "pointer",
  transition:
    "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, filter 140ms ease",
};

/** Outlined brand orange — secondary evaluation actions (Export). */
export const MAIN_PAGE_EVALUATION_SECONDARY_BUTTON_STYLE: CSSProperties = {
  marginTop: 0,
  padding: "0.55rem 1.15rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: `1px solid ${MAIN_PAGE_COLORS.processingAccent}`,
  background: "#ffffff",
  color: MAIN_PAGE_COLORS.processingAccent,
  fontSize: "0.95rem",
  fontWeight: 500,
  fontFamily: MAIN_PAGE_FONT_STACK,
  cursor: "pointer",
  transition:
    "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease",
};

export function bindMainPageEvaluationPrimaryButtonInteractions(
  eventTarget: HTMLButtonElement,
  phase: "focus" | "blur" | "enter" | "leave" | "disabled",
): void {
  if (phase === "disabled" || eventTarget.disabled) {
    eventTarget.style.filter = "none";
    eventTarget.style.boxShadow = "none";
    eventTarget.style.background = "rgba(217, 154, 67, 0.45)";
    eventTarget.style.borderColor = "rgba(217, 154, 67, 0.55)";
    eventTarget.style.cursor = "not-allowed";
    return;
  }
  if (phase === "focus") {
    eventTarget.style.outline = `2px solid ${MAIN_PAGE_COLORS.processingMid}`;
    eventTarget.style.outlineOffset = "3px";
    return;
  }
  if (phase === "blur") {
    eventTarget.style.outline = "none";
    eventTarget.style.outlineOffset = "0";
    eventTarget.style.filter = "none";
    eventTarget.style.background = MAIN_PAGE_COLORS.processingAccent;
    eventTarget.style.borderColor = MAIN_PAGE_COLORS.processingAccent;
    return;
  }
  if (phase === "enter") {
    eventTarget.style.filter = "brightness(1.06)";
    eventTarget.style.background = "#c8883a";
    eventTarget.style.borderColor = "#c8883a";
    eventTarget.style.boxShadow = "0 4px 14px rgba(217, 154, 67, 0.35)";
    return;
  }
  eventTarget.style.filter = "none";
  eventTarget.style.background = MAIN_PAGE_COLORS.processingAccent;
  eventTarget.style.borderColor = MAIN_PAGE_COLORS.processingAccent;
  eventTarget.style.boxShadow = "none";
}

export function bindMainPageEvaluationSecondaryButtonInteractions(
  eventTarget: HTMLButtonElement,
  phase: "focus" | "blur" | "enter" | "leave" | "disabled",
): void {
  if (phase === "disabled" || eventTarget.disabled) {
    eventTarget.style.background = "#ffffff";
    eventTarget.style.color = "rgba(217, 154, 67, 0.45)";
    eventTarget.style.borderColor = "rgba(217, 154, 67, 0.45)";
    eventTarget.style.cursor = "not-allowed";
    return;
  }
  if (phase === "focus") {
    eventTarget.style.outline = `2px solid ${MAIN_PAGE_COLORS.processingMid}`;
    eventTarget.style.outlineOffset = "3px";
    return;
  }
  if (phase === "blur") {
    eventTarget.style.outline = "none";
    eventTarget.style.outlineOffset = "0";
    eventTarget.style.background = "#ffffff";
    return;
  }
  if (phase === "enter") {
    eventTarget.style.background = "rgba(217, 154, 67, 0.1)";
    return;
  }
  eventTarget.style.background = "#ffffff";
}

/** In-progress primary action (Ask → Checking…) — warm amber, not warning/error. */
export const MAIN_PAGE_PROCESSING_BUTTON_STYLE: CSSProperties = {
  ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
  background: [
    "linear-gradient(180deg, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.08) 36%, rgba(255,255,255,0) 55%)",
    `linear-gradient(165deg, #F3D9A4 0%, ${MAIN_PAGE_COLORS.processingMid} 45%, ${MAIN_PAGE_COLORS.processingAccent} 100%)`,
  ].join(", "),
  color: MAIN_PAGE_COLORS.processingInk,
  border: `1px solid ${MAIN_PAGE_COLORS.processingBorder}`,
  boxShadow: [
    "inset 0 1px 0 rgba(255,255,255,0.55)",
    "inset 0 -1px 0 rgba(120, 72, 24, 0.18)",
    "0 8px 22px rgba(180, 120, 48, 0.22)",
    "0 2px 6px rgba(15, 23, 42, 0.08)",
  ].join(", "),
};

/** Destructive action — harmonized with Cognitory alert palette, not system red. */
export const MAIN_PAGE_DESTRUCTIVE_BUTTON_STYLE: CSSProperties = {
  ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
  background: [
    "linear-gradient(180deg, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.08) 36%, rgba(255,255,255,0) 55%)",
    `linear-gradient(165deg, ${MAIN_PAGE_COLORS.alertFill} 0%, rgba(254, 202, 202, 0.95) 48%, rgba(248, 113, 113, 0.92) 100%)`,
  ].join(", "),
  color: MAIN_PAGE_COLORS.alertInk,
  border: `1px solid ${MAIN_PAGE_COLORS.alertBorder}`,
  boxShadow: [
    "inset 0 1px 0 rgba(255,255,255,0.55)",
    "0 6px 18px rgba(185, 28, 28, 0.18)",
    "0 2px 6px rgba(15, 23, 42, 0.06)",
  ].join(", "),
};

/** Selected workspace mode control — soft grey secondary, not primary blue. */
export const MAIN_PAGE_SECONDARY_SELECTED_STYLE: CSSProperties = {
  background: MAIN_PAGE_COLORS.pageBackground,
  color: MAIN_PAGE_COLORS.quietInk,
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
};

/** Segmented Cog | Library track — soft grey outer pill. */
export const MAIN_PAGE_WORKSPACE_SEGMENT_TRACK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  background: "rgba(148, 163, 184, 0.22)",
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

export const MAIN_PAGE_WORKSPACE_SEGMENT_BUTTON_STYLE: CSSProperties = {
  padding: "0.45rem 1rem",
  borderRadius: MAIN_PAGE_RADIUS.capsule,
  border: "none",
  background: "transparent",
  color: MAIN_PAGE_COLORS.quietInk,
  fontSize: "0.95rem",
  fontFamily: MAIN_PAGE_FONT_STACK,
  cursor: "pointer",
  lineHeight: 1.2,
};

/** Selected segment — lighter inner pill on the grey track. */
export const MAIN_PAGE_WORKSPACE_SEGMENT_SELECTED_STYLE: CSSProperties = {
  ...MAIN_PAGE_SECONDARY_SELECTED_STYLE,
  boxShadow:
    "0 1px 3px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.65)",
};

export const MAIN_PAGE_INPUT_STYLE: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: MAIN_PAGE_RADIUS.input,
  border: `1px solid ${MAIN_PAGE_COLORS.inputBorder}`,
  padding: "0.75rem 0.85rem",
  fontFamily: MAIN_PAGE_FONT_STACK,
  fontSize: "1rem",
  lineHeight: 1.55,
  color: MAIN_PAGE_COLORS.inkStrong,
  resize: "vertical",
  background: MAIN_PAGE_COLORS.inputFill,
};

export const MAIN_PAGE_SECTION_LABEL_STYLE: CSSProperties = {
  fontSize: "0.82rem",
  fontWeight: 500,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: MAIN_PAGE_COLORS.label,
  marginBottom: "0.35rem",
  fontFamily: MAIN_PAGE_FONT_STACK,
};

/** Cog stays behind interactive content (FusionSun uses zIndex 0). */
export const MAIN_PAGE_COG_BACKGROUND_Z_INDEX = 0 as const;
export const MAIN_PAGE_CONTENT_Z_INDEX = 1 as const;

export function bindMainPageLiquidButtonInteractions(
  eventTarget: HTMLButtonElement,
  phase: "focus" | "blur" | "enter" | "leave" | "down" | "up",
): void {
  if (phase === "focus") {
    eventTarget.style.outline = "2px solid #7dd3fc";
    eventTarget.style.outlineOffset = "3px";
    return;
  }
  if (phase === "blur") {
    eventTarget.style.outline = "none";
    eventTarget.style.outlineOffset = "0";
    eventTarget.style.transform = "translateY(0)";
    eventTarget.style.filter = "none";
    return;
  }
  if (phase === "enter") {
    eventTarget.style.filter = "brightness(1.06)";
    eventTarget.style.transform = "translateY(-1px)";
    eventTarget.style.boxShadow = [
      "inset 0 1px 0 rgba(255,255,255,0.62)",
      "inset 0 -1px 0 rgba(12,74,110,0.16)",
      "0 10px 26px rgba(14, 116, 144, 0.34)",
      "0 3px 8px rgba(15, 23, 42, 0.1)",
    ].join(", ");
    eventTarget.style.borderColor = "rgba(186, 230, 253, 0.7)";
    return;
  }
  if (phase === "leave") {
    eventTarget.style.filter = "none";
    eventTarget.style.transform = "translateY(0)";
    eventTarget.style.boxShadow = String(
      MAIN_PAGE_LIQUID_BUTTON_STYLE.boxShadow,
    );
    eventTarget.style.borderColor = MAIN_PAGE_COLORS.liquidBorder;
    return;
  }
  if (phase === "down") {
    eventTarget.style.filter = "brightness(0.96)";
    eventTarget.style.transform = "translateY(1px)";
    eventTarget.style.boxShadow = [
      "inset 0 2px 4px rgba(12,74,110,0.22)",
      "inset 0 1px 0 rgba(255,255,255,0.35)",
      "0 4px 12px rgba(14, 116, 144, 0.22)",
    ].join(", ");
    return;
  }
  eventTarget.style.filter = "brightness(1.06)";
  eventTarget.style.transform = "translateY(-1px)";
  eventTarget.style.boxShadow = [
    "inset 0 1px 0 rgba(255,255,255,0.62)",
    "inset 0 -1px 0 rgba(12,74,110,0.16)",
    "0 10px 26px rgba(14, 116, 144, 0.34)",
    "0 3px 8px rgba(15, 23, 42, 0.1)",
  ].join(", ");
}

export const mainPageLiquidButtonHandlers = {
  onFocus: (event: FocusEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "focus");
  },
  onBlur: (event: FocusEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "blur");
  },
  onMouseEnter: (event: ReactMouseEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "enter");
  },
  onMouseLeave: (event: ReactMouseEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "leave");
  },
  onMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "down");
  },
  onMouseUp: (event: ReactMouseEvent<HTMLButtonElement>) => {
    bindMainPageLiquidButtonInteractions(event.currentTarget, "up");
  },
};
